'use client';

import React from 'react';
import AppIcon from '@/core/presentation/components/ui/AppIcon';
import AppLogo from '@/core/presentation/components/ui/AppLogo';
import { LogoLink } from '../components/LogoLink';
import { AppSidebar } from '../components/AppSidebar';
import { AppHeaderAccount } from '../components/AppHeaderAccount';
import type { AppSectionId } from '../../constants/sections';
import { APP_SECTION } from '../../constants/sections';
import { ROUTES, appSectionPath } from '@/app/routes';

export interface AppLayoutProps {
  currentSection: AppSectionId;
  onSectionChange: (section: AppSectionId) => void;
  /** Menu lateral (mobile): aberto/fechado — controlado pela página. */
  sidebarOpen: boolean;
  onSidebarOpenChange: (open: boolean) => void;
  /** Função de logout (ex.: sign out + redirecionar para login); definida na página */
  onLogout?: () => void | Promise<void>;
  /** Passar true enquanto o logout estiver em andamento (ex.: authState.loading) */
  isLoggingOut?: boolean;
  children: React.ReactNode;
  /** Nome da empresa do usuário (exibido no header); quando tiver auth, passar da sessão */
  companyName?: string;
  /**
   * Assinatura ok, mas teste de envio (WhatsApp) ainda não concluído — restringe navegação
   * ao fluxo de configurações e logout.
   */
  reminderIntegrationGateActive?: boolean;
}

export const AppLayout: React.FC<AppLayoutProps> = ({
  currentSection,
  onSectionChange,
  sidebarOpen,
  onSidebarOpenChange,
  onLogout,
  isLoggingOut = false,
  children,
  companyName,
  reminderIntegrationGateActive = false,
}) => {
  return (
    <div className="min-h-screen font-app text-app-body text-text-body bg-[#f0f4f8] antialiased">
      {/* Header full-width: mesma cor do sidebar, do topo até a primeira divisão; logo à esquerda, conta no canto direito da tela */}
      <header className="fixed top-0 left-0 right-0 z-50 static-gradient-bg border-b border-white/10 overflow-visible pt-[env(safe-area-inset-top,0px)]">
        <div className="h-14 flex items-center justify-between px-3 sm:px-4">
          <div className="flex items-center gap-2 min-w-0 overflow-visible">
            <button
              type="button"
              onClick={() => onSidebarOpenChange(true)}
              className="lg:hidden p-2 rounded-lg text-white/90 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan focus-visible:ring-offset-2 focus-visible:ring-offset-navy-dark shrink-0"
              aria-label="Abrir menu"
            >
              <AppIcon name="Bars3Icon" size={24} />
            </button>
            {/* Container com altura fixa: logo maior sem alterar a altura do header */}
            <div className="relative h-14 w-28 flex items-center overflow-visible shrink-0">
              <LogoLink
                href={
                  reminderIntegrationGateActive ? appSectionPath(APP_SECTION.TESTAR_ENVIO) : ROUTES.HOME
                }
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan focus-visible:ring-offset-2 focus-visible:ring-offset-navy-dark rounded-lg"
                ariaLabel="RetornaAr - Página inicial"
              >
                <AppLogo src="/assets/images/retornaArLogo.png" size={140} />
              </LogoLink>
            </div>
          </div>
          <AppHeaderAccount
            currentSection={currentSection}
            onSectionChange={onSectionChange}
            companyName={companyName}
            onSelectOption={() => onSidebarOpenChange(false)}
            onLogout={onLogout}
            isLoggingOut={isLoggingOut}
          />
        </div>
      </header>

      <AppSidebar
        currentSection={currentSection}
        onSectionChange={onSectionChange}
        isOpen={sidebarOpen}
        onClose={() => onSidebarOpenChange(false)}
        reminderIntegrationGateActive={reminderIntegrationGateActive}
      />

      {/* Área principal: offset pelo header (h-14 + safe-area superior) e pelo sidebar no desktop (lg:pl-64) */}
      <main className="pt-[calc(3.5rem+env(safe-area-inset-top,0px))] lg:pl-64 min-h-screen flex flex-col">
        <div className="flex-1 min-h-0 w-full flex flex-col px-4 pt-4 sm:px-5 sm:pt-5 lg:px-6 lg:pt-6 xl:px-8 xl:pt-8 pb-safe-content">
          {children}
        </div>
      </main>
    </div>
  );
};
